import { Box, Text, Flex, IconButton, Slider } from "@radix-ui/themes";
import React, { useRef, useState, useEffect, useContext } from "react";
import {
  PauseIcon,
} from "@radix-ui/react-icons";
import { CurrentlyPlayingContext } from "context/CurrentlyPlayingContext";

const MobileMediaPlayer = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [sliderValue, setSliderValue] = useState(0);
  const [currentTime, setCurrentTime] = useState("0:00");
  const [duration, setDuration] = useState("0:00");
  const [volume, setVolume] = useState(100);
  const [isMuted, setIsMuted] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [isRepeating, setIsRepeating] = useState(false);

  const { currentlyPlaying } = useContext(CurrentlyPlayingContext);
  const audioRef = useRef(null);

  useEffect(() => {
    const audio = audioRef.current;

    // Function to format time in minutes:seconds
    const formatTime = (time) => {
      const minutes = Math.floor(time / 60);
      const seconds = Math.floor(time % 60);
      return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
    };

    // Update the slider value and current time as the audio plays
    const updateSlider = () => {
      setSliderValue((audio.currentTime / audio.duration) * 100);
      setCurrentTime(formatTime(audio.currentTime));
    };

    // Set the duration when the audio metadata is loaded
    const setAudioDuration = () => {
      setDuration(formatTime(audio.duration));
    };

    audio.addEventListener("timeupdate", updateSlider);
    audio.addEventListener("loadedmetadata", setAudioDuration);
    audio.addEventListener("ended", handleAudioEnd); // Add event listener for audio end

    return () => {
      audio.removeEventListener("timeupdate", updateSlider);
      audio.removeEventListener("loadedmetadata", setAudioDuration);
      audio.removeEventListener("ended", handleAudioEnd); // Clean up event listener
    };
  }, []);

  // Reset play/pause button when currentlyPlaying changes aka when users selects to play a different podcast.
  useEffect(() => {
    handleAudioEnd();
  }, [currentlyPlaying]);

  const playAudio = () => {
    if (audioRef.current) {
      audioRef.current.play();
      setIsPlaying(true);
    }
  };

  const pauseAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  };

  const handlePlayPause = () => {
    if (isPlaying) {
      pauseAudio();
    } else {
      playAudio();
    }
  };

  // Add this function inside MediaPlayer
  const handleAudioEnd = () => {
    setIsPlaying(false);
    setSliderValue(0); // Reset the slider to 0
  };

  // Handle slider change (optional: seek audio position)
  const handleSliderChange = (value) => {
    const audio = audioRef.current;
    const newTime = (value[0] / 100) * audio.duration;
    audio.currentTime = newTime;
    setSliderValue(value[0]);
  };

  const handleVolumeChange = (value) => {
    setVolume(value[0]);
    if (audioRef.current) {
      audioRef.current.volume = value[0] / 100;
      audioRef.current.muted = false;
      setIsMuted(false); // Unmute if the slider is moved
    }
  };

  const toggleMute = () => {
    if (audioRef.current) {
      const muted = !audioRef.current.muted;
      audioRef.current.muted = muted;
      setIsMuted(muted);
      if (muted) {
        setVolume(0); // Set slider to 0 when muted
      } else {
        setVolume(audioRef.current.volume * 100); // Restore slider position when unmuted
      }
    }
  };

  const handleSpeedChange = () => {
    if (audioRef.current) {
      let newRate;
      if (playbackRate === 1) {
        newRate = 1.5;
      } else if (playbackRate === 1.5) {
        newRate = 2;
      } else {
        newRate = 1;
      }
      audioRef.current.playbackRate = newRate;
      setPlaybackRate(newRate);
    }
  };

  const handleSeekBackward = () => {
    if (audioRef.current) {
      const newTime = audioRef.current.currentTime - 5;
      audioRef.current.currentTime = newTime > 0 ? newTime : 0;
    }
  };

  const handleSeekForward = () => {
    if (audioRef.current) {
      const newTime = audioRef.current.currentTime + 5;
      const duration = audioRef.current.duration;
      audioRef.current.currentTime = newTime < duration ? newTime : duration;
    }
  };

  const toggleRepeat = () => {
    if (audioRef.current) {
      const newRepeatState = !isRepeating;
      audioRef.current.loop = newRepeatState;
      setIsRepeating(newRepeatState);
    }
  };

  return (
    <>
      <Box
        position="fixed"
        bottom="0"
        mb="5"
        height="100px"
        style={{
          borderRadius: "100px",
          boxShadow: "var(--shadow-6)",
          marginLeft: "4%",
        }}
      >
        <Box
          width="100%"
          height="100%"
          position="absolute"
          overflow="hidden"
          style={{
            borderRadius: "100px",
            backgroundColor: "rgb(17, 17, 19)",
            WebkitBackdropFilter: "blur(500px)",
          }}
        />
        <Flex
          height="100%"
          position="relative"
          direction="column"
          justify="center"
          align="center"
          gap="3"
        >
          <div>
            {/* Current playing */}
            {/* <Flex gap="4" align="center" p="3" style={{ width: "350px" }}>
              <Flex align="center" gap="3" style={{ paddingLeft: "10px" }}>
                <Box>
                  <Text size="1" as="div" weight="medium">
                    {currentlyPlaying.title}
                  </Text>
                </Box>
              </Flex>
            </Flex> */}

            {/* Slider  */}
            <div
              style={{
                display: "flex",
                justifyContent: "center", // Center items horizontally
                alignItems: "center", // Align items vertically
                width: "100%",
              }}
            >
              <Text size="1" color="gray" style={{ alignItems: "center" }}>
                {currentTime}
              </Text>
              <Slider
                defaultValue={[0]}
                value={[sliderValue]}
                onValueChange={handleSliderChange}
                variant="soft"
                radius="full"
                size="1"
                style={{
                  width: "250px",
                  marginTop: "5px",
                  marginLeft: "5px",
                  marginRight: "5px",
                  cursor: "pointer",
                }}
              />
              <Text size="1" color="gray" style={{ alignItems: "center" }}>
                {duration}
              </Text>
            </div>
          </div>
          {/* Controls */}
          <Flex
            align="center"
            gap="3"
            style={{
              width: "350px",
              display: "flex",
              justifyContent: "center",
            }}
          >
            {/* Playback rate button */}
            <IconButton
              color="gray"
              variant="ghost"
              radius="full"
              size="2"
              onClick={handleSpeedChange}
              style={{ cursor: "pointer" }}
            >
              <Text>{playbackRate}x</Text>
            </IconButton>
            {/* Seek back button */}
            <IconButton
              color="gray"
              variant="ghost"
              radius="full"
              size="2"
              onClick={handleSeekBackward}
              style={{ cursor: "pointer" }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="currentcolor"
                fillOpacity={0.7}
                viewBox="0 0 30 30"
                width="20"
                height="20"
                style={{ marginRight: 2 }}
              >
                <path d="M 14 6 A 1 1 0 0 0 13.408203 6.1953125 A 1 1 0 0 0 13.378906 6.2167969 L 2.4609375 14.15625 A 1 1 0 0 1 2.4589844 14.158203 L 2.4433594 14.169922 A 1 1 0 0 0 2 15 A 1 1 0 0 0 2.4492188 15.833984 L 13.40625 23.804688 A 1 1 0 0 0 14 24 A 1 1 0 0 0 15 23 A 1 1 0 0 0 15 22.996094 L 15 16.234375 L 25.40625 23.804688 A 1 1 0 0 0 26 24 A 1 1 0 0 0 27 23 A 1 1 0 0 0 27 22.996094 L 27 15 L 27 7.0039062 A 1 1 0 0 0 27 7 A 1 1 0 0 0 26 6 A 1 1 0 0 0 25.40625 6.1953125 A 1 1 0 0 0 25.378906 6.2167969 L 15 13.763672 L 15 7.0039062 A 1 1 0 0 0 15 7 A 1 1 0 0 0 14 6 z" />
              </svg>
            </IconButton>
            {/* Play Button */}
            <IconButton
              radius="full"
              size="3"
              onClick={handlePlayPause}
              style={{ cursor: "pointer" }}
            >
              {/* {isPlaying ? <PauseIcon  width="20" height="20"/> : <PlayIcon  width="20" height="20"/>} */}
              {isPlaying ? (
                <PauseIcon width="20" height="20" />
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="currentcolor"
                  viewBox="0 0 30 30"
                  width="20"
                  height="20"
                  style={{ marginRight: -2 }}
                >
                  <path d="M 6 3 A 1 1 0 0 0 5 4 A 1 1 0 0 0 5 4.0039062 L 5 15 L 5 25.996094 A 1 1 0 0 0 5 26 A 1 1 0 0 0 6 27 A 1 1 0 0 0 6.5800781 26.8125 L 6.5820312 26.814453 L 26.416016 15.908203 A 1 1 0 0 0 27 15 A 1 1 0 0 0 26.388672 14.078125 L 6.5820312 3.1855469 L 6.5800781 3.1855469 A 1 1 0 0 0 6 3 z" />
                </svg>
              )}
            </IconButton>
            {/* Seek forward button */}
            <IconButton
              color="gray"
              variant="ghost"
              radius="full"
              size="2"
              onClick={handleSeekForward}
              style={{ cursor: "pointer" }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 30 30"
                width="20"
                height="20"
                fill="currentcolor"
                fillOpacity={0.7}
                style={{ marginLeft: 2 }}
              >
                <path d="M 4 6 A 1 1 0 0 0 3 7 A 1 1 0 0 0 3 7.0039062 L 3 15 L 3 22.996094 A 1 1 0 0 0 3 23 A 1 1 0 0 0 4 24 A 1 1 0 0 0 4.5917969 23.804688 L 4.59375 23.804688 A 1 1 0 0 0 4.6210938 23.783203 L 15 16.236328 L 15 22.996094 A 1 1 0 0 0 15 23 A 1 1 0 0 0 16 24 A 1 1 0 0 0 16.591797 23.804688 L 16.59375 23.804688 A 1 1 0 0 0 16.621094 23.783203 L 27.541016 15.841797 A 1 1 0 0 0 28 15 A 1 1 0 0 0 27.556641 14.169922 L 16.59375 6.1953125 A 1 1 0 0 0 16 6 A 1 1 0 0 0 15 7 A 1 1 0 0 0 15 7.0039062 L 15 13.765625 L 4.59375 6.1953125 A 1 1 0 0 0 4 6 z" />
              </svg>
            </IconButton>
            {/* Repeat button */}
            <IconButton
              color={isRepeating ? "red" : "gray"}
              variant="ghost"
              radius="full"
              size="2"
              onClick={toggleRepeat}
              style={{ cursor: "pointer" }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 30 30"
                fill="currentcolor"
                fillOpacity={0.7}
                width="20"
                height="20"
              >
                <path d="M 20 4 L 20 7 L 8 7 C 4.6983746 7 2 9.6983746 2 13 A 1.0001 1.0001 0 1 0 4 13 C 4 10.779625 5.7796254 9 8 9 L 20 9 L 20 12 L 27 8 L 20 4 z M 26.984375 15.986328 A 1.0001 1.0001 0 0 0 26 17 C 26 19.220375 24.220375 21 22 21 L 10 21 L 10 18 L 3 22 L 10 26 L 10 23 L 22 23 C 25.301625 23 28 20.301625 28 17 A 1.0001 1.0001 0 0 0 26.984375 15.986328 z" />
              </svg>
            </IconButton>
          </Flex>
        </Flex>
      </Box>

      <div>
        <audio ref={audioRef} src={currentlyPlaying.url} />
      </div>
    </>
  );
};

export default MobileMediaPlayer;
